import { Component, Input } from '@angular/core';

import { CostSummary } from '../../interfaces/costs.interface';
import {
  CostVariation,
  abbreviateTokens,
  costToChartValue,
  costVariation,
  daysBetweenInclusive,
  formatApiDate,
  formatCost,
  formatPercent,
  formatTokens
} from '../../utils/costs-format.util';

/** Cartões de resumo do período. */
@Component({
  selector: 'app-costs-kpis',
  templateUrl: './costs-kpis.component.html',
  styleUrls: ['./costs-kpis.component.css']
})
export class CostsKpisComponent {
  @Input() summary: CostSummary | null = null;

  /**
   * Mesmo recorte, na janela imediatamente anterior. Só serve de base de comparação —
   * nenhum número dele é exibido cru.
   */
  @Input() previous: CostSummary | null = null;

  /**
   * Textos de ajuda (ⓘ) dos cards: uma frase de abertura, depois uma entrada por campo
   * exibido abaixo do número principal.
   *
   * As entradas são marcadas com `•` porque o po-tooltip só aceita texto puro — sem
   * ele, uma explicação que quebra em três linhas fica indistinguível da entrada
   * seguinte, e o balão vira um bloco corrido.
   *
   * Os `\n` só viram linha por causa do `white-space: pre-line` em `src/styles.css`, que
   * sobrescreve o `normal` padrão do po-tooltip. A regra é global de propósito, e não
   * deste componente: com `p-append-in-body` o balão é criado como filho do `<body>`,
   * onde CSS de componente não alcança (ver `costs-kpis.component.css`).
   */
  readonly cardHelp = {
    cost:
      'Soma do custo das execuções já reconciliadas no período.\n\n' +
      '• Pendentes: ainda não entram nesta conta — o total sobe conforme elas fecham.\n' +
      '• Piso não somado: gasto real de execuções cujo trace o provider truncou. Fica fora ' +
      'do total porque cobre só parte das chamadas ao LLM — o custo delas foi maior, e não ' +
      'há como saber quanto.\n' +
      '• Variação: contra o período anterior, de mesma duração.\n' +
      '• Projeção mensal: (custo ÷ dias do período) × 30. É estimativa a partir da média ' +
      'diária, não garantia; serve para comparar o ritmo entre presets (7, 30 e 90 dias ' +
      'projetam para o mesmo mês). Não aparece em "todo o período", que não tem duração fixa.',
    averages:
      'Duas médias, com denominadores diferentes.\n\n' +
      '• O valor em destaque: custo ÷ execuções reconciliadas (uma chamada ao assistente).\n' +
      '• Por review: custo ÷ reviews distintos.\n' +
      '• Reviews distintos: quantos reviews diferentes (changeset, issue, comparison, ' +
      'standalone) tiveram execução no período — cada um conta 1 vez, não importa quantas ' +
      'execuções gerou. Não é exatamente o divisor do "por review": ele conta todo review ' +
      'que teve execução, e a média divide só pelos que já têm custo apurado, então um ' +
      'review de execuções ainda pendentes aparece aqui e fica fora dela.\n\n' +
      'Ex.: uma issue de 1 arquivo revisada nos dois tipos de análise são 2 execuções; com ' +
      'um reprocessamento depois de corrigir o fix, 4 — de 1 review só. Por isso "por review" ' +
      'é sempre maior que "por execução".\n\n' +
      'Separar os dois mostra se o gasto subiu porque revisamos mais reviews ou porque cada ' +
      'um ficou mais caro; o Custo total sozinho não distingue.',
    tokens:
      'Consumo real de tokens, apurado na reconciliação.\n\n' +
      '• Entrada / saída: tokens separados por direção — o preço por token difere entre elas.\n' +
      '• Total exato: o número completo, sem a abreviação (M) do valor em destaque.\n' +
      '• Reaproveitado de cache: quanto da entrada o provider informa ter servido de cache. ' +
      'Explica queda de custo sem queda de uso.\n' +
      '• Auto-reporte do provider: o que ele informa sozinho ao responder, cobrindo só a ' +
      'última chamada ao LLM de cada execução.\n\n' +
      'Os dois últimos somam TODAS as execuções do período, inclusive as ainda não ' +
      'reconciliadas — o provider os devolve na hora da resposta. Já o valor em destaque e ' +
      'entrada/saída contam só as reconciliadas. Por isso o auto-reporte não é uma fatia do ' +
      'número acima, e com muita execução pendente pode até passar dele.',
    runs:
      'Execuções do assistente no período. Cada arquivo gera uma execução em cada tipo de ' +
      'análise, e reprocessar gera execuções novas — por isso este número é bem maior que o ' +
      'de reviews.\n\n' +
      '• Reconciliadas: já têm custo apurado; entram no Custo total.\n' +
      '• Custo parcial: o provider truncou o trace e parte das chamadas ao LLM se perdeu. ' +
      'O que sobrou é gasto real, mas só o piso — fica fora do Custo total.\n' +
      '• Sem custo apurável: não vão fechar — ou a reconciliação desistiu, ou a execução não ' +
      'tem trace no provider.'
  };

  get currency(): string {
    return this.summary?.currency || 'USD';
  }

  /**
   * Período efetivamente consultado, lido do que a resposta ecoa — não do filtro da
   * tela. Cada ponta é opcional na API: vazia significa sem limite daquele lado, e as
   * duas vazias, todo o histórico.
   */
  get periodLabel(): string {
    if (!this.summary) { return '—'; }
    const { dateFrom, dateTo } = this.summary;
    if (!dateFrom && !dateTo) { return 'Todo o período'; }
    if (!dateFrom) { return `Até ${formatApiDate(dateTo)}`; }
    if (!dateTo) { return `A partir de ${formatApiDate(dateFrom)}`; }
    return `${formatApiDate(dateFrom)} a ${formatApiDate(dateTo)}`;
  }

  // ─── Custo ───────────────────────────────────────────────────────────────

  get costLabel(): string {
    return formatCost(this.summary?.cost, this.currency);
  }

  /**
   * Gasto observado nas execuções de trace truncado. Aparece separado, e nunca somado
   * ao total: é piso do que aquelas execuções custaram, não o custo delas.
   */
  get hasPartialCost(): boolean {
    return (this.summary?.runs?.partial ?? 0) > 0;
  }

  /**
   * Só o dinheiro. Quantas execuções são já é resposta do card "Execuções", que mostra
   * "Custo parcial: N execuções" na mesma linha da tela — repetir a contagem aqui era o
   * que estourava o rótulo para duas linhas, e ainda dizia a mesma coisa três vezes com
   * o texto da exportação logo acima.
   */
  get partialCostLabel(): string {
    return formatCost(this.summary?.costPartial, this.currency);
  }

  /** Variação contra a janela anterior. Null quando não há base de comparação. */
  get costChange(): CostVariation | null {
    if (!this.summary || !this.previous) { return null; }
    return costVariation(this.summary.cost, this.previous.cost);
  }

  get previousCostLabel(): string {
    return formatCost(this.previous?.cost, this.currency);
  }

  /**
   * Run-rate mensal: média diária do período (custo ÷ dias) × 30 — não os dias que
   * faltam pro período fechar (os presets são janelas móveis terminando hoje, não têm
   * "resto"), mas o ritmo atual normalizado pra 30 dias, comparável entre presets
   * diferentes (7/30/90 dias todos projetam pro mesmo mês).
   *
   * Só com período fechado dos dois lados — "todo o período" não tem quantidade de
   * dias definida pra tirar a média, e projetar sobre isso seria inventar número.
   * `costToChartValue`: mesma exceção documentada nela, é estimativa por construção.
   */
  get monthlyRunRateLabel(): string | null {
    const dateFrom = this.summary?.dateFrom;
    const dateTo = this.summary?.dateTo;
    if (!this.summary || !dateFrom || !dateTo) { return null; }

    const days = daysBetweenInclusive(dateFrom, dateTo);
    if (days <= 0) { return null; }

    const dailyAverage = costToChartValue(this.summary.cost) / days;
    return formatCost((dailyAverage * 30).toFixed(6), this.currency);
  }

  // ─── Custo médio ─────────────────────────────────────────────────────────

  get costPerRunLabel(): string {
    return formatCost(this.summary?.averages?.perRun, this.currency);
  }

  get costPerResourceLabel(): string {
    return formatCost(this.summary?.averages?.perResource, this.currency);
  }

  /** Variação do custo por execução — o indicador de eficiência, não de volume. */
  get costPerRunChange(): CostVariation | null {
    if (!this.summary?.averages || !this.previous?.averages) { return null; }
    return costVariation(this.summary.averages.perRun, this.previous.averages.perRun);
  }

  // ─── Tokens ──────────────────────────────────────────────────────────────

  get tokensLabel(): string {
    return abbreviateTokens(this.summary?.tokens?.total);
  }

  get tokensExactLabel(): string {
    return formatTokens(this.summary?.tokens?.total);
  }

  /**
   * Entrada e saída juntas num rótulo só, com o peso de cada uma. Separadas em cards
   * diferentes viravam dois números sem relação aparente.
   *
   * Os valores vão na ordem do rótulo e sem repetir as palavras "entrada"/"saída", que
   * já estão nele: com elas o texto passava de uma linha dentro do card (que é 1/4 da
   * largura da tela) e quebrava com recuo, para dizer duas vezes a mesma coisa.
   */
  get tokenSplitLabel(): string {
    const tokens = this.summary?.tokens;
    if (!tokens?.total) { return ''; }
    return `${abbreviateTokens(tokens.prompt)} (${formatPercent(tokens.prompt, tokens.total)}) · ` +
      `${abbreviateTokens(tokens.completion)}`;
  }

  get hasCachedTokens(): boolean {
    return (this.summary?.tokens?.cached ?? 0) > 0;
  }

  get cachedTokensLabel(): string {
    return formatTokens(this.summary?.tokens?.cached);
  }

  /**
   * Gap de auto-reporte, sempre com rótulo. `reported` nunca aparece cru ao lado de
   * `total`: sem a explicação viram dois números de consumo sem diferença aparente.
   */
  get reportedGapLabel(): string {
    const tokens = this.summary?.tokens;
    if (!tokens?.total) { return ''; }
    return `${formatPercent(tokens.reported, tokens.total)} do consumo real`;
  }

  // ─── Execuções ───────────────────────────────────────────────────────────

  get runsTotal(): number {
    return this.summary?.runs?.total ?? 0;
  }

  get reconciledLabel(): string {
    const runs = this.summary?.runs;
    if (!runs) { return '—'; }
    return `${formatTokens(runs.reconciled)} de ${formatTokens(runs.total)}`;
  }

  /** Reviews distintos, do contador real de chaves (o breakdown tem teto de fatias). */
  get distinctReviewsLabel(): string {
    const keys = this.summary?.breakdownKeys?.resource;
    if (keys != null) { return formatTokens(keys); }
    const resources = this.summary?.breakdown?.resource;
    return resources ? `${resources.length}` : '—';
  }

  /**
   * O aviso de incompletude olha **só** `pending`. `abandoned` e `untracked` são estados
   * finais: incluí-los deixaria o alerta permanente, e alerta permanente vira ruído.
   */
  get pendingRuns(): number {
    return this.summary?.runs?.pending ?? 0;
  }

  get pendingWarning(): string {
    const pending = this.pendingRuns;
    const plural = pending === 1 ? 'execução ainda não reconciliada' : 'execuções ainda não reconciliadas';
    return `${formatTokens(pending)} ${plural} — os valores tendem a subir.`;
  }

  get partialRunsLabel(): string {
    const runs = this.summary?.runs?.partial ?? 0;
    const plural = runs === 1 ? 'execução' : 'execuções';
    return `${formatTokens(runs)} ${plural}`;
  }

  /** Estados finais, sinalizados com texto próprio e sem cara de alerta. */
  get unaccountableRuns(): number {
    const runs = this.summary?.runs;
    return (runs?.abandoned ?? 0) + (runs?.untracked ?? 0);
  }

  get unaccountableLabel(): string {
    const total = this.unaccountableRuns;
    const plural = total === 1 ? 'execução' : 'execuções';
    return `${total} ${plural}`;
  }
}
